import React, {useContext, useEffect, useState} from "react";
import {Line} from "react-chartjs-2";
import { useRef } from "react";
import {uniqueUsers} from "../../util/Helpers";
import styled from "styled-components";
import {Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend } from 'chart.js';
import {StatsContext} from "../context/StatsContext";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend);

const Options = styled.div`
    display: flex;
    justify-content: space-evenly;
    align-items: center;
    & > label {
        display: flex;
        align-items: center;
        gap: 0.5em;
    }
`

const colors = [
    '#ef0029',
    '#377eb8',
    '#66a61e',
    '#984ea3',
    '#00d2d5',
    '#ff7f00',
    '#af8d00',
    '#7f80cd',
    '#b3e900',
    '#c42e60',
    '#a65628',
    '#f781bf',
    '#8dd3c7',
    '#fb8072',
];

const defaultData = {
    labels: [],
    datasets: [],
};

const defaultOptions = {
    responsive: true,
    scales: {
        y: {
            beginAtZero: true,
        },
    },
    plugins: {
        legend: {
            position: 'top',
        },
        tooltip: {
            enabled: true,
            interaction: {
                mode: 'index',
            },
            xAlign: 'right',
        },
    },
};

const customOptions = {
    scales: {
        y: {
        ticks: {
            callback: function(value) {
            return value.toFixed(2); // Always show two decimals
            }
        }
        }
    },
    plugins: {
        tooltip: {
            enabled: true,
            interaction: {
                mode: 'index',
            },
            intersect: true,
            callbacks: {
                label: function(context) {
                let value = context.parsed.y;
                return "€" + value.toFixed(2).replace(".", ",");
                }
            }
        },
    },
};

const AveragePricesPerMonthChart = () => {
    const chartRef = useRef();

    const { averageCostsPerMonth, getAverageCostsPerMonth } = useContext(StatsContext);

    const [data, setData] = useState(defaultData);
    const [allMonths, setAllMonths] = useState([]);
    const [visibleMonths, setVisibleMonths] = useState([]);
    const [startMonth, setStartMonth] = useState("");
    const [endMonth, setEndMonth] = useState("");
    const [showAverage, setShowAverage] = useState(false); // default off
    const [yMin, setYMin] = useState("");
    const [yMax, setYMax] = useState("");

    const options = {
        ...defaultOptions,
        ...customOptions,
        scales: {
            ...defaultOptions.scales,
            ...customOptions.scales,
            y: {
                ...defaultOptions.scales.y,
                ...customOptions.scales.y,
                min: yMin !== "" ? Number(yMin) : undefined,
                max: yMax !== "" ? Number(yMax) : undefined,
            }
        }
    };

    useEffect(() => {
        getAverageCostsPerMonth();
    }, []);

    useEffect(() => {
        const months = [...new Set(averageCostsPerMonth.map(x => x.month))].sort();
        setAllMonths(months);
        setStartMonth(months[months.length - 12]);
        setEndMonth(months[months.length - 1]);
    }, [averageCostsPerMonth]);

    useEffect(() => {
        let months = allMonths;
        if (startMonth)
            months = months.filter(month => month >= startMonth);
        if (endMonth)
            months = months.filter(month => month <= endMonth);

        setVisibleMonths(months);
    }, [startMonth, endMonth, allMonths]);

    useEffect(() => {
        const users = uniqueUsers(averageCostsPerMonth.filter(x => visibleMonths.includes(x.month)).map(x => x.user));
        const getDataOfUser = (user, color) => {
            const data = visibleMonths.map(month => {
                const dataPoint = averageCostsPerMonth.find(x => x.user.id === user.id && x.month === month);
                return dataPoint ? dataPoint.price : null;
            });
            return { label: user.username, data, backgroundColor: color };
        };

        // Average price per month dataset
        const averagePrices = visibleMonths.map(month => {
            const monthData = averageCostsPerMonth.filter(x => x.month === month);
            if (monthData.length === 0) return null;
            const sum = monthData.reduce((acc, curr) => acc + curr.price, 0);
            return (sum / monthData.length).toFixed(2);
        });

        let datasets = [];
        // Always include all datasets, but control their visibility
        const userDatasets = users.map((u, i) => getDataOfUser(u, colors[i]));
        const averageDataset = {
            type: 'line',
            label: 'Gemiddelde prijs',
            data: averagePrices,
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
            borderColor: 'rgba(0, 0, 0, 0.7)',
            borderWidth: 1,
            yAxisID: 'y',
        };

        if (showAverage) {
            // Hide all user datasets, show average
            datasets = [
                { ...averageDataset, hidden: false },
                ...userDatasets.map(ds => ({ ...ds, hidden: true }))
            ];
        } else {
            // Show all user datasets, hide average
            datasets = [
                { ...averageDataset, hidden: true },
                ...userDatasets.map(ds => ({ ...ds, hidden: false }))
            ];
        }
        setData({ labels: visibleMonths, datasets });
    }, [visibleMonths, averageCostsPerMonth, showAverage]);

    const handleStart = (event) => {
        setStartMonth(event.target.value);
    };

    const handleEnd = (event) => {
        setEndMonth(event.target.value);
    };

    return (
        <>
            <Line
                key={showAverage + '-' + visibleMonths.join(',')}
                ref={chartRef}
                options={options}
                data={data}
                type={'line'}
            />
            <Options>
                <label>
                    <input
                        type="checkbox"
                        checked={showAverage}
                        onChange={e => setShowAverage(e.target.checked)}
                        />
                    Alleen gemiddelde prijs
                </label>
                <label>
                    Start
                    <select value={startMonth} onChange={(e) => handleStart(e)}>
                        {allMonths.map(month => (<option key={month} value={month}>{month}</option>))}
                    </select>
                </label>
                <label>
                    End
                    <select value={endMonth} onChange={(e) => handleEnd(e)}>
                        {allMonths.map(month => (<option key={month} value={month}>{month}</option>))}
                    </select>
                </label>
            </Options>
            <Options style={{marginTop: "1em"}}>
                <label>
                    Minimum y-as waarde
                    <input
                        type="number"
                        step="0.01"
                        value={yMin}
                        onChange={e => setYMin(e.target.value)}
                        style={{width: "5em"}}
                        placeholder="auto"
                    />
                </label>
                <label>
                    Maximale y-as waarde
                    <input
                        type="number"
                        step="0.01"
                        value={yMax}
                        onChange={e => setYMax(e.target.value)}
                        style={{width: "5em"}}
                        placeholder="auto"
                    />
                </label>
            </Options>
        </>
    );
}

export default AveragePricesPerMonthChart;
